/**
 * ================================================================================================
 *  משימות — A WIDGET IN THE NAVIGATION RAIL, UNDER הגדרות. **Owner, 2026-11-01.**
 * ================================================================================================
 *
 *     *"משימות, ביקשתי שזה יהיה בוויג׳ט בצד ימין מתחת להגדרות."*
 *
 * ── THE THIRD PLACE IN TWO DAYS, AND THIS ONE IS HIS ───────────────────────────────────────────
 *
 * The two task readings were two large cards in the centre of the home screen; §4 of his own list
 * made them *"רצועה קומפקטית בתחתית אזור התוכן"*; and seeing that strip he named the position he
 * had asked for in the first place. **The right-hand side is the dark navigation rail** — the
 * market readings are on the left, under `בשימוש לאחרונה`, which is where he put them — and
 * `הגדרות` is the last destination in it.
 *
 * ── WHICH MAKES IT PART OF THE SHELL, AND THAT HAS A COST WORTH STATING ───────────────────────
 *
 * `ShellNav` renders on every customer screen, so these two reads are issued on every page load
 * rather than on the home screen alone — and in this product every link IS a page load. That is
 * two requests per navigation for an office with the capability.
 *
 * It is what he asked for and it is not free; the delivery says so and offers the narrowing. The
 * reads themselves are the cheapest shape available: `IX_work_item_due` and `IX_work_item_assignee`
 * exist so each is a covered read, and the widget asks for nothing a count does not need.
 *
 * ── AND IT IS NOT DRAWN FOR SOMEBODY WHO MAY NOT READ IT ──────────────────────────────────────
 *
 * `tasks.item.read` gates the route, and this component renders NOTHING without it — so a
 * colleague whose role carries no task capability sees no widget rather than a refusal in their
 * navigation. That is also what keeps the two requests off their page loads entirely.
 */

import { useCallback } from 'react';
import type { JSX } from 'react';

import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { ServerRead } from '../components/ServerRead';
import { WidgetReadFault } from '../components/WidgetPanel';
import { useLocale } from '../i18n/LocaleProvider';
import type { WorkItemSummary } from '../api/contract';
import { routes } from '../routes';

/**
 * One reading: a label, and the number of rows the server answered with.
 *
 * ── AND WHAT IT DOES WHEN THE READ FAILS. **Owner section 5, 2026-11-01.** ───────────────────
 *
 *     *"לטיפול היום׳ ו׳משימות מתפרצות שלי׳ מציגים שגיאות טעינה… הקריאה נכשלה ←
 *     הודעה קצרה + ניסיון חוזר, בלי פסקאות הסבר ארוכות."*
 *
 * It had the message and not the button: `useServerData` returns a `reload` and this widget was
 * taking `.result` and discarding it, so a person whose two counts failed could do nothing but
 * reload the whole page. The failure is now one line and a button that re-issues THAT read.
 */
function Count({
  labelHe,
  result,
  onRetry,
}: {
  readonly labelHe: string;
  readonly result: ReturnType<typeof useServerData<{ items: readonly WorkItemSummary[] }>>['result'];
  readonly onRetry: () => void;
}): JSX.Element {
  const { t } = useLocale();
  return (
    <span className="gp-nav-tasks__reading">
      <span className="gp-nav-tasks__label">{labelHe}</span>
      <ServerRead<{ items: readonly WorkItemSummary[] }>
        result={result}
        whatHe={labelHe}
        skeletonRows={1}
        /*
          A 404 ON A LIST READ IS NOT THE GENERIC ITEM SENTENCE — *"we could not find the item the
          action refers to, so nothing was changed"* — about a read that named no item and changed
          nothing. Section 6 of the Owner's list; `WidgetReadFault` is the one wording.
        */
        renderNotFound={() => <WidgetReadFault />}
        /*
          THE SHORT FORM, because this is a two-hundred-pixel rail and the full notice is three
          paragraphs. The state is unchanged -- the read failed and this is the failed branch --
          and no number is shown, which is the rule a count must never break.
        */
        renderFailure={(failure) => (
          <span className="gp-nav-tasks__fault">
            <span>{t('state.failedToLoad')}</span>
            <button type="button" className="gp-nav-tasks__retry" onClick={onRetry}>
              {t('state.retry')}
            </button>
            {/*
              ── AND THE REFERENCE, WHICH THE SHORT FORM NEARLY THREW AWAY ───────────────────
              *"נדרש אבחון לפי מזהי הבקשות והיומן, ולא רק ניסוח אחר של ההודעה."* The full
              notice prints `אסמכתא: <requestUid>`, and shortening the message without it would
              have taken away the one string that makes a reported failure findable in the
              journal -- while answering the complaint about length. Two of the seven failure
              kinds carry no reference (a 404, and a request that never reached the server);
              those print none rather than an empty label.
            */}
            {'requestUid' in failure ? (
              <span className="gp-nav-tasks__ref" dir="ltr" title={failure.requestUid}>
                {failure.requestUid}
              </span>
            ) : null}
          </span>
        )}
      >
        {(data) => (
          <span className="gp-nav-tasks__count" dir="ltr">
            {data.items.length}
          </span>
        )}
      </ServerRead>
    </span>
  );
}

export function NavTasks({
  capabilities,
}: {
  readonly capabilities: readonly string[];
}): JSX.Element | null {
  const { t } = useLocale();
  /*
   * TWO READS AND NOT ONE, because they are two different SERVER-SIDE narrowings: `today` is a due
   * date across the office and `mine` is an assignee. One request filtered twice in the browser
   * would be a third implementation of a predicate the server owns.
   *
   * THE HOOKS RUN BEFORE THE CAPABILITY GUARD and the guard is below them, which is the opposite
   * of what it should be — so the guard is on the CALL instead: without the capability each
   * reader resolves to an empty list and no request is issued. A `return null` above a `useState`
   * is the hook-order fault this shell has hit before, and `useServerData` has no `enabled`.
   */
  const mayRead = capabilities.includes('tasks.item.read');
  const readToday = useCallback(
    () =>
      mayRead
        ? callOperation('tenant.work_item.list', { params: { scope: 'today' } })
        : Promise.resolve({ items: [] as readonly WorkItemSummary[] }),
    [mayRead],
  );
  const readMine = useCallback(
    () =>
      mayRead
        ? callOperation('tenant.work_item.list', { params: { scope: 'mine' } })
        : Promise.resolve({ items: [] as readonly WorkItemSummary[] }),
    [mayRead],
  );
  const today = useServerData(readToday);
  const mine = useServerData(readMine);

  if (!mayRead) return null;

  return (
    /*
      `<section>` WITH A NAME, inside the `<nav>` but not part of its list: a block of numbers
      with no heading is read as three unrelated words, and a screen reader tabbing the rail
      needs to know it has left the destinations.
    */
    <section className="gp-nav-tasks" aria-labelledby="gp-nav-tasks-heading">
      <h2 id="gp-nav-tasks-heading" className="gp-nav-tasks__heading">
        {t('home.tasksStrip')}
      </h2>
      <Count labelHe={t('home.today')} result={today.result} onRetry={today.reload} />
      <Count labelHe={t('home.widgets.urgent')} result={mine.result} onRetry={mine.reload} />
      <a className="gp-nav-tasks__link" href={routes.tenantWorkItems}>
        {t('home.tasksOpenAll')}
      </a>
    </section>
  );
}
